import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, Type } from '@angular/core';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, ParamMap, NavigationEnd } from '@angular/router';
import { Observable, Subscription, filter, finalize, of } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from 'src/environments/environment';

// Injector
import { AppInjector } from 'src/app/app.component';

// Services
import { AppService } from 'src/app/@core/services/app.service';
import { ServiceBase } from 'src/app/@core/services/service-base';
import { AccountEntryExportService } from 'src/app/@core/services/accounting/account-entry-export.service';
import { AccountEntryExportFormatService } from 'src/app/@core/services/accounting/account-entry-export-format.service';
import { AccountEntryService } from 'src/app/@core/services/accounting/account-entry.service';
import { AccountEntryLineService } from 'src/app/@core/services/accounting/account-entry-line.service';
import { AccountEntryNumberRangeService } from 'src/app/@core/services/accounting/account-entry-number-range.service';
import { AccountingMethodService } from 'src/app/@core/services/accounting/accounting-method.service';
import { AccountingPeriodService } from 'src/app/@core/services/accounting/accounting-period.service';
import { AccountingSettingsService } from 'src/app/@core/services/accounting/accounting-settings.service';
import { AccountService } from 'src/app/@core/services/accounting/account.service';
import { AddressService } from 'src/app/@core/services/address/address.service';
import { AgentMarkerService } from 'src/app/@core/services/ai/agent-marker.service';
import { AiAssistantService } from 'src/app/@core/services/ai/ai-assistant.service';
import { AiSettingsService } from 'src/app/@core/services/ai/ai-settings.service';
import { AnalyticalAxisService } from 'src/app/@core/services/analytical/analytical-axis.service';
import { AnalyticalDefaultService } from 'src/app/@core/services/analytical/analytical-default.service';
import { AnalyticalKeyService } from 'src/app/@core/services/analytical/analytical-key.service';
import { AnalyticalSectionService } from 'src/app/@core/services/analytical/analytical-section.service';
import { AnalyticalVentilationService } from 'src/app/@core/services/analytical/analytical-ventilation.service';
import { AnnouncementService } from 'src/app/@core/services/announcements/announcement.service';
import { ApiKeyService } from 'src/app/@core/services/client/api-key.service';
import { AssistantAuthorizationService } from 'src/app/@core/services/interfaces/assistant-authorization.service';
import { AssistantGrantService } from 'src/app/@core/services/interfaces/assistant-grant.service';
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';
import { BankService } from 'src/app/@core/services/banking/bank.service';
import { BankAccountService } from 'src/app/@core/services/banking/bank-account.service';
import { BankAccountLineService } from 'src/app/@core/services/banking/bank-account-line.service';
import { BankAccountLineTypeService } from 'src/app/@core/services/banking/bank-account-line-type.service';
import { BankAccountRuleService } from 'src/app/@core/services/banking/bank-account-rule.service';
import { BankAccountTypeService } from 'src/app/@core/services/banking/bank-account-type.service';
import { BankingSettingsService } from 'src/app/@core/services/banking/banking-settings.service';
import { BankStatementService } from 'src/app/@core/services/banking/bank-statement.service';
import { BankStatementAutomationService } from 'src/app/@core/services/banking/bank-statement-automation.service';
import { BankStatementLineService } from 'src/app/@core/services/banking/bank-statement-line.service';
import { CardReceiptService } from 'src/app/@core/services/banking/card-receipt.service';
import { CashTransactionService } from 'src/app/@core/services/banking/cash-transaction.service';
import { ChequeService } from 'src/app/@core/services/banking/cheque.service';
import { ChequeRefusalReasonService } from 'src/app/@core/services/banking/cheque-refusal-reason.service';
import { CityService } from 'src/app/@core/services/address/city.service';
import { CivilityService } from 'src/app/@core/services/user/civility.service';
import { ClientService } from 'src/app/@core/services/client/client.service';
import { ClientAccountEntryTypeService } from 'src/app/@core/services/accounting/client-account-entry-type.service';
import { ClientContactService } from 'src/app/@core/services/client/contact.service';
import { ClientTaxService } from 'src/app/@core/services/accounting/client-tax.service';
import { ClientUserService } from 'src/app/@core/services/client/user.service';
import { ConfirmationServiceBroker } from 'src/app/@core/services/confirmation.service';
import { ConversationService } from 'src/app/@core/services/messaging/conversation.service';
import { ConversationItemService } from 'src/app/@core/services/messaging/conversation-item.service';
import { CountryService } from 'src/app/@core/services/core/country.service';
import { CurrencyService } from 'src/app/@core/services/core/currency.service';
import { CreditCardService } from 'src/app/@core/services/banking/credit-card.service';
import { CreditCardTransactionService } from 'src/app/@core/services/banking/credit-card-transaction.service';
import { DepositService } from 'src/app/@core/services/banking/deposit.service';
import { DialogConfigService } from 'src/app/@core/services/dialog-config.service';
import { DiscountTypeService } from 'src/app/@core/services/sales/discount-type.service';
import { DocumentationService } from 'src/app/@core/services/documentation/documentation.service';
import { EcoTaxSectorService } from 'src/app/@core/services/product/eco-tax-sector.service';
import { EmailService } from 'src/app/@core/services/messaging/email.service';
import { EmailRedirectionService } from 'src/app/@core/services/interfaces/email-redirection.service';
import { EmployeeService } from 'src/app/@core/services/human-resources/employee.service';
import { EstablishmentService } from 'src/app/@core/services/partner/establishment.service';
import { FileService } from 'src/app/@core/services/files/file.service';
import { FocusService } from 'src/app/@core/services/focus.service';
import { FrequencyService } from 'src/app/@core/services/core/frequency.service';
import { ImportBatchService } from 'src/app/@core/services/imports/import-batch.service';
import { InviteService } from 'src/app/@core/services/client/invite.service';
import { InvoiceContactService } from 'src/app/@core/services/sales/contact.service';
import { InvoiceNumberingService } from 'src/app/@core/services/sales/invoice-numbering.service';
import { InvoiceTypeService } from 'src/app/@core/services/invoicing/invoice-type.service';
import { ItemTypeService } from 'src/app/@core/services/sales/item-type.service';
import { JournalService } from 'src/app/@core/services/accounting/journal.service';
import { JournalTypeService } from 'src/app/@core/services/accounting/journal-type.service';
import { KpiService } from 'src/app/@core/services/kpi/kpi.service';
import { LedgerEntryService } from 'src/app/@core/services/settlement/ledger-entry.service';
import { LegalStatusService } from 'src/app/@core/services/taxation/legal-status.service';
import { LoanService } from 'src/app/@core/services/banking/loan.service';
import { LoanInstalmentService } from 'src/app/@core/services/banking/loan-instalment.service';
import { FcmDeviceService } from 'src/app/@core/services/notifications/fcm-device.service';
import { MfaService } from 'src/app/@core/authentication/services/mfa.service';
import { NotificationSettingsService } from 'src/app/@core/services/notifications/notification-settings.service';
import { NumericComparatorService } from 'src/app/@core/services/core/numeric-comparator.service';
import { OnboardingService } from 'src/app/@core/services/onboarding/onboarding.service';
import { OnboardingStepService } from 'src/app/@core/services/onboarding/step.service';
import { OrganizationService } from 'src/app/@core/services/legal-entity/organization.service';
import { OrganizationActivityService } from 'src/app/@core/services/legal-entity/organization-activity.service';
import { OrganizationIdentifierTypeService } from 'src/app/@core/services/legal-entity/organization-identifier-type.service';
import { OrganizationSizeService } from 'src/app/@core/services/legal-entity/organization-size.service';
import { PackageService } from 'src/app/@core/services/subscription/package.service';
import { PartnerService } from 'src/app/@core/services/partner/partner.service';
import { PartnerContactService } from 'src/app/@core/services/partner/contact.service';
import { PartnerGroupService } from 'src/app/@core/services/partner/partner-group.service';
import { PartnerTypeService } from 'src/app/@core/services/partner/partner-type.service';
import { PaymentMethodService } from 'src/app/@core/services/core/payment-method.service';
import { PaymentProviderService } from 'src/app/@core/services/banking/payment-provider.service';
import { PaymentScheduleService } from 'src/app/@core/services/sales/payment-schedule.service';
import { PaymentTermService } from 'src/app/@core/services/invoicing/payment-term.service';
import { PersonNameOrderService } from 'src/app/@core/services/core/person-name-order.service';
import { PhoneAreaCodeService } from 'src/app/@core/services/address/phone-area-code.service';
import { PricingModeService } from 'src/app/@core/services/core/pricing-mode.service';
import { ProductService } from 'src/app/@core/services/product/product.service';
import { ProductGroupService } from 'src/app/@core/services/product/product-group.service';
import { ProductSectionTitleService } from 'src/app/@core/services/product/product-section-title.service';
import { ProductTypeService } from 'src/app/@core/services/product/product-type.service';
import { AuthorizationService } from 'src/app/@core/services/authorizations/authorization.service';
import { ProfileService } from 'src/app/@core/services/authorizations/profile.service';
import { ClientProfileMfaService } from 'src/app/@core/services/authorizations/client-profile-mfa.service';
import { CustomProfileService } from 'src/app/@core/services/authorizations/custom-profile.service';
import { PipelinePollingService } from 'src/app/@core/services/pipeline-polling.service';
import { PurchaseInvoiceService } from 'src/app/@core/services/purchasing/purchase-invoice.service';
import { QrLoginService } from 'src/app/@core/authentication/services/qr-login.service';
import { QuestionService } from 'src/app/@core/services/help/question.service';
import { QuotationService } from 'src/app/@core/services/sales/quotation.service';
import { QuotationItemService } from 'src/app/@core/services/sales/quotation-item.service';
import { RecurringInvoiceService } from 'src/app/@core/services/sales/recurring-invoice/recurring-invoice.service';
import { RecurringInvoiceEndingModeService } from 'src/app/@core/services/sales/recurring-invoice/ending-mode.service';
import { RemunerationService } from 'src/app/@core/services/human-resources/remuneration.service';
import { SalesDocumentTemplatesService } from 'src/app/@core/services/sales/templates.service';
import { SalesInvoiceService } from 'src/app/@core/services/sales/sales-invoice.service';
import { SalesSettingsService } from 'src/app/@core/services/sales/sales-settings.service';
import { SellingInfoRecordService } from 'src/app/@core/services/sales/selling-info-record.service';
import { SettlementService } from 'src/app/@core/services/settlement/settlement.service';
import { ShareholderService } from 'src/app/@core/services/client/shareholder.service';
import { SubscriptionService } from 'src/app/@core/services/subscription/subscription.service';
import { SubsidyService } from 'src/app/@core/services/accounting/subsidy.service';
import { TaxDetailsService } from 'src/app/@core/services/core/tax-detail.service';
import { TaxesService } from 'src/app/@core/services/core/tax.service';
import { TaxationRegimeService } from 'src/app/@core/services/accounting/taxation-regime.service';
import { TaxationSettingsService } from 'src/app/@core/services/taxation/taxation-settings.service';
import { TaxationTypeService } from 'src/app/@core/services/accounting/taxation-type.service';
import { TgcDeclarationService } from 'src/app/@core/services/taxation/tgc-declaration.service';
import { TimezoneService } from 'src/app/@core/services/core/timezone.service';
import { TransactionDirectionService } from 'src/app/@core/services/core/transaction-direction.service';
import { UnitService } from 'src/app/@core/services/core/unit.service';
import { UserService } from 'src/app/@core/services/user/user.service';
import { VatChargeabilityService } from 'src/app/@core/services/accounting/vat-chargeability.service';
import { VatFrequencyService } from 'src/app/@core/services/accounting/vat-frequency.service';
import { TableStateService } from 'src/app/@core/services/table-state.service';

// Modèles
import { File as FileModel } from '../../models/file/file.model';
import { ModelBase } from '../../models/abstract/model-base.model';

// Enums
import { ActivitiesEnum } from '../../enums/activities.enum';
import { AuthorizationsEnum } from 'src/app/@shared/enums/authorizations.enum';
import { FeaturesEnum } from '../../enums/features.enum';
import { PackagesEnum } from '../../enums/packages.enum';
import { QuotasEnum } from '../../enums/quotas.enum';
import { StatusEnum } from 'src/app/@shared/enums/status.enum';

// Utilities
import { Sorter } from '../../utilities/models/sorter';
import { Logger } from 'src/app/@core/services/logger/logger.service';
import { QueryOptions } from '../../utilities/odata/query-params';

const LOGGER = new Logger('BaseComponent');

/**
 * Ligne désignée par l'adresse, sur une liste dont le détail s'affiche dans une fenêtre (#995).
 * Sa valeur est l'identifiant technique de la ligne.
 */
export const SELECTED_QUERY_PARAM = 'selected';

/**
 * Fil de discussion désigné par l'adresse, ouvert par-dessus la fenêtre de détail (#995).
 * Ne vaut jamais seul : il accompagne `SELECTED_QUERY_PARAM`.
 */
export const CONVERSATION_QUERY_PARAM = 'conversation';

/**
 * Objet source des lignes d'écriture à mettre en évidence sur un grand livre (#1135).
 * Sa valeur est `<type>:<identifiant>`, le type étant une valeur de `ContentTypesEnum`.
 */
export const HIGHLIGHTED_QUERY_PARAM = 'highlighted';

@Component({
    selector: 'biifor-app-base',
    template: `
        NO UI TO BE FOUND HERE!
    `,
    host: {
        class: 'h-full w-full block'
    },
    standalone: false
})
export abstract class BaseComponent implements OnInit, OnDestroy {
    // Environnement
    Environment = environment;
    // Appareil
    public deviceService: DeviceDetectorService;
    public titleService: Title;
    // Routing
    protected navBackUrl: string | null = null;
    protected location: Location;
    protected router: Router;
    private _route: ActivatedRoute;
    // URL params
    protected queryParams!: ParamMap;
    protected routeParams!: ParamMap;
    // Enums
    public Activities = ActivitiesEnum;
    public Authorizations = AuthorizationsEnum;
    public Features = FeaturesEnum;
    public Packages = PackagesEnum;
    public Quotas = QuotasEnum;
    public Status = StatusEnum;
    // Services
    public appService: AppService;
    protected accountEntryExportService: AccountEntryExportService;
    protected accountEntryExportFormatService: AccountEntryExportFormatService;
    protected accountEntryService: AccountEntryService;
    protected accountEntryLineService: AccountEntryLineService;
    protected accountEntryNumberRangeService: AccountEntryNumberRangeService;
    protected accountingMethodService: AccountingMethodService;
    protected accountingPeriodService: AccountingPeriodService;
    protected accountingSettingsService: AccountingSettingsService;
    protected accountService: AccountService;
    protected addressService: AddressService;
    protected agentMarkerService: AgentMarkerService;
    protected aiAssistantService: AiAssistantService;
    protected aiSettingsService: AiSettingsService;
    protected analyticalAxisService: AnalyticalAxisService;
    protected analyticalDefaultService: AnalyticalDefaultService;
    protected analyticalKeyService: AnalyticalKeyService;
    protected analyticalSectionService: AnalyticalSectionService;
    protected analyticalVentilationService: AnalyticalVentilationService;
    protected announcementService: AnnouncementService;
    protected apiKeyService: ApiKeyService;
    protected assistantAuthorizationService: AssistantAuthorizationService;
    protected assistantGrantService: AssistantGrantService;
    protected authenticationService: AuthenticationService;
    protected bankService: BankService;
    protected bankAccountService: BankAccountService;
    protected bankAccountLineService: BankAccountLineService;
    protected bankAccountLineTypeService: BankAccountLineTypeService;
    protected bankAccountRuleService: BankAccountRuleService;
    protected bankAccountTypeService: BankAccountTypeService;
    protected bankingSettingsService: BankingSettingsService;
    protected bankStatementService: BankStatementService;
    protected bankStatementAutomationService: BankStatementAutomationService;
    protected bankStatementLineService: BankStatementLineService;
    protected cardReceiptService: CardReceiptService;
    protected cashTransactionService: CashTransactionService;
    protected chequeService: ChequeService;
    protected chequeRefusalReasonService: ChequeRefusalReasonService;
    protected cityService: CityService;
    protected civilityService: CivilityService;
    protected clientService: ClientService;
    protected clientAccountEntryTypeService: ClientAccountEntryTypeService;
    protected clientContactService: ClientContactService;
    protected clientTaxService: ClientTaxService;
    protected clientUserService: ClientUserService;
    protected conversationService: ConversationService;
    protected conversationItemService: ConversationItemService;
    protected countryService: CountryService;
    protected currencyService: CurrencyService;
    protected creditCardService: CreditCardService;
    protected creditCardTransactionService: CreditCardTransactionService;
    protected depositService: DepositService;
    protected discountTypeService: DiscountTypeService;
    protected documentationService: DocumentationService;
    protected ecoTaxSectorService: EcoTaxSectorService;
    protected emailService: EmailService;
    protected emailRedirectionService: EmailRedirectionService;
    protected employeeService: EmployeeService;
    protected establishmentService: EstablishmentService;
    protected fileService: FileService;
    protected focusService: FocusService;
    protected frequencyService: FrequencyService;
    protected importBatchService: ImportBatchService;
    protected inviteService: InviteService;
    protected invoiceContactService: InvoiceContactService;
    protected invoiceNumberingService: InvoiceNumberingService;
    protected invoiceTypeService: InvoiceTypeService;
    protected itemTypeService: ItemTypeService;
    protected journalService: JournalService;
    protected journalTypeService: JournalTypeService;
    protected kpiService: KpiService;
    protected ledgerEntryService: LedgerEntryService;
    protected legalStatusService: LegalStatusService;
    protected loanService: LoanService;
    protected loanInstalmentService: LoanInstalmentService;
    protected fcmDeviceService: FcmDeviceService;
    protected mfaService: MfaService;
    protected notificationSettingsService: NotificationSettingsService;
    protected numericComparatorService: NumericComparatorService;
    protected onboardingService: OnboardingService;
    protected onboardingStepService: OnboardingStepService;
    protected organizationService: OrganizationService;
    protected organizationActivityService: OrganizationActivityService;
    protected organizationIdentifierTypeService: OrganizationIdentifierTypeService;
    protected organizationSizeService: OrganizationSizeService;
    protected packageService: PackageService;
    protected partnerService: PartnerService;
    protected partnerContactService: PartnerContactService;
    protected partnerGroupService: PartnerGroupService;
    protected partnerTypeService: PartnerTypeService;
    protected paymentMethodService: PaymentMethodService;
    protected paymentProviderService: PaymentProviderService;
    protected paymentScheduleService: PaymentScheduleService;
    protected paymentTermService: PaymentTermService;
    protected personNameOrderService: PersonNameOrderService;
    protected phoneAreaCodeService: PhoneAreaCodeService;
    protected pricingModeService: PricingModeService;
    protected productService: ProductService;
    protected productGroupService: ProductGroupService;
    protected productSectionTitleService: ProductSectionTitleService;
    protected productTypeService: ProductTypeService;
    protected authorizationService: AuthorizationService;
    protected clientProfileMfaService: ClientProfileMfaService;
    protected profileService: ProfileService;
    protected customProfileService: CustomProfileService;
    protected pipelinePollingService: PipelinePollingService;
    protected purchaseInvoiceService: PurchaseInvoiceService;
    protected qrLoginService: QrLoginService;
    protected questionService: QuestionService;
    protected quotationService: QuotationService;
    protected quotationItemService: QuotationItemService;
    protected recurringInvoiceService: RecurringInvoiceService;
    protected recurringInvoiceEndingModeService: RecurringInvoiceEndingModeService;
    protected remunerationService: RemunerationService;
    protected salesDocumentTemplatesService: SalesDocumentTemplatesService;
    protected salesInvoiceService: SalesInvoiceService;
    protected salesSettingsService: SalesSettingsService;
    protected sellingInfoRecordService: SellingInfoRecordService;
    protected settlementService: SettlementService;
    protected shareholderService: ShareholderService;
    protected subscriptionService: SubscriptionService;
    protected subsidyService: SubsidyService;
    protected taxDetailService: TaxDetailsService;
    protected taxService: TaxesService;
    protected taxationRegimeService: TaxationRegimeService;
    protected taxationSettingsService: TaxationSettingsService;
    protected taxationTypeService: TaxationTypeService;
    protected tgcDeclarationService: TgcDeclarationService;
    protected timezoneService: TimezoneService;
    protected transactionDirectionService: TransactionDirectionService;
    protected unitService: UnitService;
    protected userService: UserService;
    protected vatChargeabilityService: VatChargeabilityService;
    protected vatFrequencyService: VatFrequencyService;
    protected dialogConfigService: DialogConfigService;
    protected tableStateService: TableStateService;
    private _confirmationService: ConfirmationServiceBroker;
    // Subscriptions
    public subscriptions: Subscription = new Subscription();
    // Observer
    protected observeComponent: boolean = false;
    protected observerThreshold: number = 0.1;
    protected observerRootMargin: string = '0px';
    protected _isVisible: boolean = false;
    protected _wasVisible: boolean = false;
    protected _observer: IntersectionObserver | null = null;
    private _elementRef = inject(ElementRef);
    // Options de requête
    public filtersString: string = '';
    protected queryOptions: QueryOptions = new QueryOptions();
    public totalRecords: number = 0;
    private _previousUrlWithoutSelected: string | null = null;
    // View attributes
    @Input()
    public busy: boolean = false;  // Action en cours côté front
    private _disabled: boolean = false;
    public get disabled(): boolean {
        return this._disabled;
    }
    @Input()
    public set disabled(disabled: boolean) {
        this._disabled = disabled;
    }
    protected _loading: boolean = false;  // Action en cours côté back
    public get loading(): boolean {
        return this._loading;
    }
    @Input()
    public set loading(loading: boolean) {
        this._loading = loading;
    }
    @Input()
    public tooltipDelay = 100;
    @Input()
    public tooltipPosition = 'bottom';
    public uploadCurrent: number = 0;
    public uploadTotal: number = 0;
    // Gestion du drag and drop
    public dragover = false;
    private _keepDragover = false;
    // Évènement
    @Output()
    filesUploaded = new EventEmitter();

    constructor() {
        // Device
        this.deviceService = AppInjector.get(DeviceDetectorService);
        this.titleService = AppInjector.get(Title);
        // Routing
        this.location = AppInjector.get(Location);
        this.router = AppInjector.get(Router);
        this._route = AppInjector.get(ActivatedRoute);
        // Services
        this.appService = AppInjector.get(AppService);
        this.accountEntryExportService = AppInjector.get(AccountEntryExportService);
        this.accountEntryExportFormatService = AppInjector.get(AccountEntryExportFormatService);
        this.accountEntryService = AppInjector.get(AccountEntryService);
        this.accountEntryLineService = AppInjector.get(AccountEntryLineService);
        this.accountEntryNumberRangeService = AppInjector.get(AccountEntryNumberRangeService);
        this.accountingMethodService = AppInjector.get(AccountingMethodService);
        this.accountingPeriodService = AppInjector.get(AccountingPeriodService);
        this.accountingSettingsService = AppInjector.get(AccountingSettingsService);
        this.accountService = AppInjector.get(AccountService);
        this.addressService = AppInjector.get(AddressService);
        this.agentMarkerService = AppInjector.get(AgentMarkerService);
        this.aiAssistantService = AppInjector.get(AiAssistantService);
        this.aiSettingsService = AppInjector.get(AiSettingsService);
        this.analyticalAxisService = AppInjector.get(AnalyticalAxisService);
        this.analyticalDefaultService = AppInjector.get(AnalyticalDefaultService);
        this.analyticalKeyService = AppInjector.get(AnalyticalKeyService);
        this.analyticalSectionService = AppInjector.get(AnalyticalSectionService);
        this.analyticalVentilationService = AppInjector.get(AnalyticalVentilationService);
        this.announcementService = AppInjector.get(AnnouncementService);
        this.apiKeyService = AppInjector.get(ApiKeyService);
        this.assistantAuthorizationService = AppInjector.get(AssistantAuthorizationService);
        this.assistantGrantService = AppInjector.get(AssistantGrantService);
        this.authenticationService = AppInjector.get(AuthenticationService);
        this.authorizationService = AppInjector.get(AuthorizationService);
        this.bankService = AppInjector.get(BankService);
        this.bankAccountService = AppInjector.get(BankAccountService);
        this.bankAccountLineService = AppInjector.get(BankAccountLineService);
        this.bankAccountLineTypeService = AppInjector.get(BankAccountLineTypeService);
        this.bankAccountRuleService = AppInjector.get(BankAccountRuleService);
        this.bankAccountTypeService = AppInjector.get(BankAccountTypeService);
        this.bankingSettingsService = AppInjector.get(BankingSettingsService);
        this.bankStatementService = AppInjector.get(BankStatementService);
        this.bankStatementAutomationService = AppInjector.get(BankStatementAutomationService);
        this.bankStatementLineService = AppInjector.get(BankStatementLineService);
        this.cardReceiptService = AppInjector.get(CardReceiptService);
        this.cashTransactionService = AppInjector.get(CashTransactionService);
        this.chequeService = AppInjector.get(ChequeService);
        this.chequeRefusalReasonService = AppInjector.get(ChequeRefusalReasonService);
        this.cityService = AppInjector.get(CityService);
        this.civilityService = AppInjector.get(CivilityService);
        this.clientService = AppInjector.get(ClientService);
        this.clientAccountEntryTypeService = AppInjector.get(ClientAccountEntryTypeService);
        this.clientContactService = AppInjector.get(ClientContactService);
        this.clientTaxService = AppInjector.get(ClientTaxService);
        this.clientUserService = AppInjector.get(ClientUserService);
        this.conversationService = AppInjector.get(ConversationService);
        this.conversationItemService = AppInjector.get(ConversationItemService);
        this.countryService = AppInjector.get(CountryService);
        this.currencyService = AppInjector.get(CurrencyService);
        this.creditCardService = AppInjector.get(CreditCardService);
        this.creditCardTransactionService = AppInjector.get(CreditCardTransactionService);
        this.depositService = AppInjector.get(DepositService);
        this.discountTypeService = AppInjector.get(DiscountTypeService);
        this.documentationService = AppInjector.get(DocumentationService);
        this.ecoTaxSectorService = AppInjector.get(EcoTaxSectorService);
        this.emailService = AppInjector.get(EmailService);
        this.emailRedirectionService = AppInjector.get(EmailRedirectionService);
        this.employeeService = AppInjector.get(EmployeeService);
        this.establishmentService = AppInjector.get(EstablishmentService);
        this.fileService = AppInjector.get(FileService);
        this.focusService = AppInjector.get(FocusService);
        this.frequencyService = AppInjector.get(FrequencyService);
        this.importBatchService = AppInjector.get(ImportBatchService);
        this.inviteService = AppInjector.get(InviteService);
        this.invoiceContactService = AppInjector.get(InvoiceContactService);
        this.invoiceNumberingService = AppInjector.get(InvoiceNumberingService);
        this.invoiceTypeService = AppInjector.get(InvoiceTypeService);
        this.itemTypeService = AppInjector.get(ItemTypeService);
        this.journalService = AppInjector.get(JournalService);
        this.journalTypeService = AppInjector.get(JournalTypeService);
        this.kpiService = AppInjector.get(KpiService);
        this.ledgerEntryService = AppInjector.get(LedgerEntryService);
        this.legalStatusService = AppInjector.get(LegalStatusService);
        this.loanService = AppInjector.get(LoanService);
        this.loanInstalmentService = AppInjector.get(LoanInstalmentService);
        this.fcmDeviceService = AppInjector.get(FcmDeviceService);
        this.mfaService = AppInjector.get(MfaService);
        this.notificationSettingsService = AppInjector.get(NotificationSettingsService);
        this.numericComparatorService = AppInjector.get(NumericComparatorService);
        this.onboardingService = AppInjector.get(OnboardingService);
        this.onboardingStepService = AppInjector.get(OnboardingStepService);
        this.organizationService = AppInjector.get(OrganizationService);
        this.organizationActivityService = AppInjector.get(OrganizationActivityService);
        this.organizationIdentifierTypeService = AppInjector.get(OrganizationIdentifierTypeService);
        this.organizationSizeService = AppInjector.get(OrganizationSizeService);
        this.packageService = AppInjector.get(PackageService);
        this.partnerService = AppInjector.get(PartnerService);
        this.partnerContactService = AppInjector.get(PartnerContactService);
        this.partnerGroupService = AppInjector.get(PartnerGroupService);
        this.partnerTypeService = AppInjector.get(PartnerTypeService);
        this.paymentMethodService = AppInjector.get(PaymentMethodService);
        this.paymentProviderService = AppInjector.get(PaymentProviderService);
        this.paymentScheduleService = AppInjector.get(PaymentScheduleService);
        this.paymentTermService = AppInjector.get(PaymentTermService);
        this.personNameOrderService = AppInjector.get(PersonNameOrderService);
        this.phoneAreaCodeService = AppInjector.get(PhoneAreaCodeService);
        this.pricingModeService = AppInjector.get(PricingModeService);
        this.productService = AppInjector.get(ProductService);
        this.productGroupService = AppInjector.get(ProductGroupService);
        this.productSectionTitleService = AppInjector.get(ProductSectionTitleService);
        this.productTypeService = AppInjector.get(ProductTypeService);
        this.clientProfileMfaService = AppInjector.get(ClientProfileMfaService);
        this.profileService = AppInjector.get(ProfileService);
        this.customProfileService = AppInjector.get(CustomProfileService);
        this.pipelinePollingService = AppInjector.get(PipelinePollingService);
        this.purchaseInvoiceService = AppInjector.get(PurchaseInvoiceService);
        this.qrLoginService = AppInjector.get(QrLoginService);
        this.questionService = AppInjector.get(QuestionService);
        this.quotationService = AppInjector.get(QuotationService);
        this.quotationItemService = AppInjector.get(QuotationItemService);
        this.recurringInvoiceService = AppInjector.get(RecurringInvoiceService);
        this.recurringInvoiceEndingModeService = AppInjector.get(RecurringInvoiceEndingModeService);
        this.remunerationService = AppInjector.get(RemunerationService);
        this.salesDocumentTemplatesService = AppInjector.get(SalesDocumentTemplatesService);
        this.salesInvoiceService = AppInjector.get(SalesInvoiceService);
        this.salesSettingsService = AppInjector.get(SalesSettingsService);
        this.sellingInfoRecordService = AppInjector.get(SellingInfoRecordService);
        this.settlementService = AppInjector.get(SettlementService);
        this.shareholderService = AppInjector.get(ShareholderService);
        this.subscriptionService = AppInjector.get(SubscriptionService);
        this.subsidyService = AppInjector.get(SubsidyService);
        this.taxDetailService = AppInjector.get(TaxDetailsService);
        this.taxService = AppInjector.get(TaxesService);
        this.taxationRegimeService = AppInjector.get(TaxationRegimeService);
        this.taxationSettingsService = AppInjector.get(TaxationSettingsService);
        this.taxationTypeService = AppInjector.get(TaxationTypeService);
        this.tgcDeclarationService = AppInjector.get(TgcDeclarationService);
        this.timezoneService = AppInjector.get(TimezoneService);
        this.transactionDirectionService = AppInjector.get(TransactionDirectionService);
        this.unitService = AppInjector.get(UnitService);
        this.userService = AppInjector.get(UserService);
        this.vatChargeabilityService = AppInjector.get(VatChargeabilityService);
        this.vatFrequencyService = AppInjector.get(VatFrequencyService);
        this.dialogConfigService = AppInjector.get(DialogConfigService);
        this.tableStateService = AppInjector.get(TableStateService);
        this._confirmationService = AppInjector.get(ConfirmationServiceBroker);
    }


    // Tri par défaut
    protected defaultSorters: Sorter[] = [
        new Sorter({
            property: 'id'
        })
    ];

    /*----------------------------------------------------------------------------------------
     * Getters / setters
     ----------------------------------------------------------------------------------------*/

    public get metaKey(): string {
        // Use modern userAgentData API if available, fallback to userAgent parsing
        if ('userAgentData' in navigator && (navigator as any).userAgentData?.platform) {
            return (navigator as any).userAgentData.platform.toLowerCase().includes('mac') ? 'cmd' : 'ctrl';
        } else {
            // Fallback to userAgent parsing
            return navigator.userAgent.toLowerCase().includes('mac') ? 'cmd' : 'ctrl';
        }
    }

    public get route(): ActivatedRoute {
        let route = this._route;
        while (!!route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    public get taxNameLabel(): string {
        return this.appService.selectedClient?.isCaledonian ? 'TGC' : 'TVA';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    ngOnInit() {
        // Gestion des paramètres de route
        this.subscriptions.add(
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd)
            ).subscribe(() => {
                this.afterNavigation(false);
            })
        );

        // Initialisation après la première navigation
        this.afterNavigation(true);

        // Souscription aux observables
        this.subscribeToObservables();
    }

    ngAfterViewInit() {
        if (this.observeComponent) {
            this.setupIntersectionObserver();
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this._observer) {
            this._observer.disconnect();
        }
    }

    /**
     * L'utilisateur peut-il gérer les pièces jointes de cet objet ?
     * Activité dédiée `manage_attachments`, avec repli sur `update` pour les porteurs
     * qui ne la déclarent pas — cf. `AppService.canManageAttachments`.
     */
    public canManageAttachments(object: ModelBase): boolean {
        return this.appService.canManageAttachments(object);
    }

    public canUserPerform(activity: string, object: ModelBase): boolean {
        return this.appService.canUserPerform(activity, object);
    }

    public getFormattedContent(text: string): string {
        return text.replace(/\n/g, '<br>');
    }

    public getServiceForEntityType(entityType: string): ServiceBase {
        switch (entityType) {
            case 'purchase_invoice':
                return this.purchaseInvoiceService;
            case 'sales_invoice':
                return this.salesInvoiceService;
            case 'quotation':
                return this.quotationService;
            case 'bank_account_line':
                return this.bankAccountLineService;
            case 'cash_transaction':
                return this.cashTransactionService;
            case 'credit_card_transaction':
                return this.creditCardTransactionService;
            case 'card_receipt':
                return this.cardReceiptService;
            case 'cheque':
                return this.chequeService;
            case 'deposit':
                return this.depositService;
            case 'Account':
                return this.accountService;
            case 'Country':
                return this.countryService;
            case 'Currency':
                return this.currencyService;
            case 'PaymentTerm':
                return this.paymentTermService;
            case 'Unit':
                return this.unitService;
            default:
                throw (`No service found for entity type: ${entityType}`);
        }
    }

    /**
     * Returns true if application is used on an Android device
     */
    public isAndroid(): boolean {
        const ANDROID: string = 'android';
        let os: string = this.deviceService.os.toLowerCase();
        return os.includes(ANDROID);
    }

    public onBlur(event: Event): void {
        const target = event.target as HTMLElement;
        if (target && target.id) {
            this.focusService.setLastFocusedElementId(target.id);
            console.log('Blur de ' + target.id);
        }
    }

    public onClickNavBack() {
        this.navBack();
    }

    /**
     * Comportement par défaut du clic sur un onglet
     *
     * Cela permet de mettre à jour le paramètre d'URL afin d'ajouter l'onglet actif
     * @param event
     */
    public onClickTab(event: any): void {
        // Récupérer la valeur depuis l'élément tab cliqué
        const tabValue = event.target.getAttribute('value');

        // Mettre à jour le paramètre d'URL sans recharger la page
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: tabValue },
            queryParamsHandling: 'merge' // préserve les autres paramètres d'URL existants
        });
    }

    public onDownload(file: FileModel) {
        this.fileService.downloadFile$(file.id).subscribe(blob => {
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = file.fileName;
            link.click();

            window.URL.revokeObjectURL(link.href);
        });
    }

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragover = true;
        this._keepDragover = true;
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this._keepDragover = false;
        setTimeout(() => {
            if (!this._keepDragover) {
                this.dragover = false;
            }
        }, 10);
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent) {
        this.dragover = false;
        let files = event.dataTransfer!.files;
        if (!files || files.length === 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        // Conversion en liste de File
        let filesArray = Array.from(files) as File[];
        let count = filesArray.length;
        if (filesArray.length > 0) {
            this.uploadFiles(filesArray, 1, count);
        }
    }

    /**
     * Évènement déclenché par l'utilisation d'un fileupload
     * @param event
     */
    public onUpload(event: any, callback?: Function) {
        const files = event.files;
        let count = files.length;
        this.uploadFiles(files, 1, count, callback);
    }

    public preventDefault(event: Event) {
        event.preventDefault();
    }

    public stopPropagation(event: Event) {
        event.stopPropagation();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected afterNavigation(initial: boolean): void {
        let message = initial ? 'Initial navigation detected.' : 'Subsequent navigation detected.';
        LOGGER.debug(`[${this.constructor.name}] ${message}. URL: ${this.router.url}`);
        this._processUrl();
    }

    protected buildQueryOptions(): void {
        this.queryOptions = QueryOptions.fromUrlString(this.router.url);

        // Si aucun tri n'est défini, appliquer le tri par défaut
        if ((!this.queryOptions.sorters || this.queryOptions.sorters.length === 0) && this.defaultSorters) {
            this.queryOptions.sorters = this.defaultSorters;
        }
    }

    protected clearAllServicesCache() {
        // Boucle sur tous les attributs de la classe qui héritent de ServiceBase
        for (let key in this) {
            if (this[key] instanceof ServiceBase) {
                (this[key] as ServiceBase).clearCache();
            }
        }
    }

    protected componentVisibleCallback() {
        LOGGER.info('Component is visible');
    }

    protected confirmAction(action: string, callback: Function, rejectCallback?: Function) {
        this._confirmationService.confirm(action, callback, rejectCallback);
    }

    protected filesUploadFinalized() {
        LOGGER.info('Upload finalisé. Redéfinir la méthode filesUploadFinalized() dans le composant pour un traitement spécialisé');
    }

    protected focusNextElement(el: HTMLElement): void {
        const focusableElements = Array.from(document.querySelectorAll('input, button, select, textarea, [tabindex]:not([tabindex="-1"])'))
            .filter((el): el is HTMLElement => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
        const currentIndex = focusableElements.indexOf(el);
        if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
            const nextElement = focusableElements[currentIndex + 1] as HTMLElement;
            nextElement.focus();
        }
    }

    protected loadData() { }

    protected mapParams(): void {
        if (this.queryParams.has('returnTo')) {
            this.navBackUrl = this.queryParams.get('returnTo') || null;
        }
    }

    protected navBack(): void {
        if (this.navBackUrl) {
            this.router.navigateByUrl(this.navBackUrl);
        } else {
            this.location.back();
        }
    }

    /**
     * Méthode permettant l'affichage d'une fenêtre de dialogue dynamique
     * avec pour contenu le composant passé en paramètre
     * @param component Le composant à afficher
     * @param config Le nom de la configuration à utiliser
     * @param data Les données à passer au composant
     * @returns L'id de la fenêtre de dialogue, cet identifiant peut être utilisé pour capter les évènements de la fenêtre
     */
    protected openDialog(component: Type<any>, config: string, data: any = {}): string {
        return this.dialogConfigService.show(component, config, data);
    }

    /**
     * Convertit une query string en objet de paramètres
     */
    protected parseQueryString(queryString: string): { [key: string]: any } {
        if (!queryString || queryString.length === 0) {
            return {};
        }

        // Enlever le "?" si présent
        if (queryString.startsWith('?')) {
            queryString = queryString.substring(1);
        }

        const params: { [key: string]: any } = {};
        const pairs = queryString.split('&');

        for (const pair of pairs) {
            const [key, value] = pair.split('=');
            if (key) {
                params[key] = value ? decodeURIComponent(value) : '';
            }
        }

        return params;
    }

    /**
     * Méthode permettant l'affichage d'une facture dans une fenêtre de dialogue
     * @param invoice La facture à afficher
     * @returns L'id de la fenêtre de dialogue
     */
    protected async openPdfViewerDialog(invoice: any): Promise<string> {
        try {
            const { PdfViewerDialogComponent } = await import(
                'src/app/@shared/components/pdf/pdf-viewer-dialog/pdf-viewer-dialog.component'
            );
            return this.openDialog(PdfViewerDialogComponent, 'PDF_VIEWER', { invoice: invoice });
        } catch (error) {
            console.error('Erreur lors du chargement du composant PdfViewerDialog', error);
            return '';
        }
    }

    /**
     * Méthode permettant l'affichage d'un PDF dans une fenêtre de dialogue à partir d'une URL pre-signed
     * @param pdfUrl L'URL pre-signed du PDF à afficher
     * @param header Le titre de la fenêtre de dialogue
     * @returns L'id de la fenêtre de dialogue
     */
    protected async openPdfViewerDialogFromUrl(pdfUrl: string, header: string): Promise<string> {
        try {
            const { PdfViewerDialogComponent } = await import(
                'src/app/@shared/components/pdf/pdf-viewer-dialog/pdf-viewer-dialog.component'
            );
            return this.openDialog(PdfViewerDialogComponent, 'PDF_VIEWER', { pdfUrl, header });
        } catch (error) {
            console.error('Erreur lors du chargement du composant PdfViewerDialog', error);
            return '';
        }
    }

    protected rememberFocus(): void {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.id) {
            this.focusService.setLastFocusedElementId(activeElement.id);
            console.log('Mémorisation focus sur ' + activeElement.id);
        }
    }

    /**
     * Inscrit — ou retire — le fil de discussion affiché dans l'adresse (#995).
     *
     * **Miroir partiel** : l'adresse ne désigne qu'un fil qui **existe** côté serveur. Le fil
     * public « Commentaires » est virtuel tant que personne n'y a écrit (cf. #36) : il n'a
     * aucune identité adressable, et le paramètre disparaît alors purement et simplement.
     *
     * En `replaceUrl` : passer d'un fil à l'autre n'est pas un déplacement dans l'application,
     * et empiler une entrée d'historique par clic rendrait le bouton *Précédent* inutilisable.
     */
    protected setConversationInUrl(conversationId: number | null): void {
        const current = this.router.parseUrl(this.router.url).queryParams[CONVERSATION_QUERY_PARAM] ?? null;
        const target = conversationId ? String(conversationId) : null;
        if (current === target) {
            return;
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [CONVERSATION_QUERY_PARAM]: target },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    // Après que la vue ait été mise à jour, on réaffecte le focus si nécessaire
    protected setFocus(): void {
        const lastFocusedElementId = this.focusService.getLastFocusedElementId();
        if (lastFocusedElementId) {
            const el = document.getElementById(lastFocusedElementId);
            if (el) {
                this.focusNextElement(el);
                this.focusService.clearLastFocusedElementId();
            }
        }
    }

    protected setupIntersectionObserver() {
        const options = {
            root: null,
            rootMargin: this.observerRootMargin,
            threshold: this.observerThreshold
        };

        this._observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this._isVisible = entry.isIntersecting;
                if (this._isVisible && !this._wasVisible) {
                    // Se déclenche lorsque le composant devient visible uniquement
                    this._wasVisible = true;
                    this.componentVisibleCallback();
                }
            });
        }, options);

        // Nécessite que ElementRef soit injecté dans BaseComponent
        this._observer.observe(this._elementRef.nativeElement);
    }

    protected subscribeToObservables() { }

    protected uploadFile$(file: File): Observable<any> {
        LOGGER.warn('Méthode uploadFile$ non implémentée dans le composant', this.constructor.name);
        LOGGER.warn('Fichier', file);
        return of(false);
    }

    protected uploadFiles(files: File[], current: number, total: number, callback?: Function) {
        this.uploadCurrent = current;
        this.uploadTotal = total;
        const file = files.shift();

        if (!callback) {
            callback = this.uploadFile$.bind(this);
        }

        callback(file!).pipe(
            finalize(() => {
                if (files.length > 0) {
                    this.uploadFiles(files, current + 1, total, callback);
                } else {
                    this.uploadCurrent = 0;
                    this.uploadTotal = 0;
                    this.filesUploadFinalized();
                    this.filesUploaded.emit();
                }
            })
        ).subscribe(() => { });
    }

    /**
     * URL courante privée des paramètres qui désignent une ligne et son fil de discussion.
     * Ces deux paramètres ouvrent une fenêtre par-dessus la liste : ils ne changent pas les
     * données affichées, et ne doivent donc pas déclencher un rechargement.
     */
    private _getUrlWithoutSelected(): string {
        const urlTree = this.router.parseUrl(this.router.url);
        const queryParams = { ...urlTree.queryParams };
        delete queryParams[SELECTED_QUERY_PARAM];
        delete queryParams[CONVERSATION_QUERY_PARAM];

        urlTree.queryParams = queryParams;
        return this.router.serializeUrl(urlTree);
    }

    private _processUrl(): void {
        // Mapping des éléments de l'URL
        const route = this.route;
        this.routeParams = route.snapshot.paramMap;
        this.queryParams = route.snapshot.queryParamMap;
        this.mapParams();
        this.buildQueryOptions();

        // Construire l'URL sans le paramètre 'selected'
        const currentUrlWithoutSelected = this._getUrlWithoutSelected();

        // Ne charger les données que si l'URL a changé (hors paramètre 'selected')
        if (this._previousUrlWithoutSelected !== currentUrlWithoutSelected) {
            this._previousUrlWithoutSelected = currentUrlWithoutSelected;
            this.loadData();
        }
    }
}
